import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Building2, Users, Briefcase, ArrowRight, CheckCircle2,
  Database, FileSpreadsheet, Code2, Send, Sparkles, Zap,
  ShieldCheck, Receipt, Bot,
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";

type AudienceKey = "agency" | "blogger" | "brand";

interface AudienceContent {
  key: AudienceKey;
  tabLabel: string;
  tabIcon: typeof Building2;
  title: string;
  subtitle: string;
  benefits: { icon: typeof CheckCircle2; title: string; description: string }[];
  cta: { label: string; mobileLabel: string; to: string };
}

const AUDIENCES: AudienceContent[] = [
  {
    key: "agency",
    tabLabel: "Агентства",
    tabIcon: Building2,
    title: "Масштабируйте отчётность",
    subtitle: "Управляйте сотнями креативов клиентов в один поток. Без рутины и ручных таблиц.",
    benefits: [
      {
        icon: FileSpreadsheet,
        title: "Массовая загрузка актов",
        description: "Импорт договоров и креативов пачкой — XML EnterpriseData 1.17 для 1С в один клик.",
      },
      {
        icon: Database,
        title: "Автоматизация передачи в ЕРИР",
        description: "ИИ-агент сам формирует и отправляет ежемесячные отчёты в Реестр интернет-рекламы.",
      },
      {
        icon: Code2,
        title: "API для ваших систем",
        description: "REST API и webhooks для интеграции с CRM, биллингом и внутренними дашбордами.",
      },
    ],
    cta: { label: "Открыть тариф «Агентство»", mobileLabel: "Открыть тарифы", to: "/pricing" },
  },
  {
    key: "blogger",
    tabLabel: "Блогеры",
    tabIcon: Users,
    title: "Маркируйте на лету",
    subtitle: "Получайте erid за секунды прямо из мессенджера. Без юристов, без бюрократии.",
    benefits: [
      {
        icon: Send,
        title: "Telegram-бот для ERID",
        description: "Отправили ссылку на пост — получили токен и пометку «Реклама» обратно в чат.",
      },
      {
        icon: Sparkles,
        title: "Никакой бюрократии",
        description: "ИП или самозанятый — система сама подставит нужные поля для ОРД и ЕРИР.",
      },
      {
        icon: Zap,
        title: "Безопасные нативные интеграции",
        description: "Сканер ФЗ-168 проверит креатив рекламодателя до публикации и сохранит вашу репутацию.",
      },
    ],
    cta: { label: "Попробовать бесплатно", mobileLabel: "Открыть тарифы", to: "/auth" },
  },
  {
    key: "brand",
    tabLabel: "Бренды",
    tabIcon: Briefcase,
    title: "Защита и прозрачность",
    subtitle: "Полный контроль над рекламным бюджетом и нулевой риск штрафов от ФАС и РКН.",
    benefits: [
      {
        icon: Bot,
        title: "ИИ-защита от ботов и скликивания",
        description: "ERIDON анализирует трафик инфлюенсеров и подсвечивает аномалии до выплаты.",
      },
      {
        icon: Receipt,
        title: "Налоговая прозрачность 3% (479-ФЗ)",
        description: "Автоматический расчёт и формирование квитанций по налогу на интернет-рекламу.",
      },
      {
        icon: ShieldCheck,
        title: "Исключение рисков штрафов",
        description: "Каждый креатив проходит лингвоаудит ФЗ-168 и проверку маркировки до запуска.",
      },
    ],
    cta: { label: "Связаться с экспертом", mobileLabel: "Открыть тарифы", to: "/pricing" },
  },
];

export function AudienceTabs() {
  return (
    <section className="w-full max-w-5xl mx-auto px-4">
      <div className="mb-8 text-center">
        <div
          className="mb-3 inline-block rounded-full border border-primary/30 bg-primary/10 px-4 py-1.5 text-[10px] font-semibold uppercase tracking-widest"
          style={{ color: "hsl(var(--primary-light))" }}
        >
          Решения для вашего бизнеса
        </div>
        <h2 className="text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
          Выберите свою роль —{" "}
          <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
            ERIDON подстроится
          </span>
        </h2>
        <p className="mx-auto mt-3 max-w-2xl text-sm text-muted-foreground sm:text-base">
          Под каждый тип клиента — свой сценарий маркировки, своя автоматизация, свой набор интеграций.
        </p>
      </div>

      <Tabs defaultValue="agency" className="w-full">
        <TabsList className="mx-auto mb-8 grid h-auto w-full max-w-2xl grid-cols-1 gap-1.5 rounded-2xl bg-secondary/50 p-1.5 sm:grid-cols-3 sm:gap-1">
          {AUDIENCES.map((aud) => {
            const Icon = aud.tabIcon;
            return (
              <TabsTrigger
                key={aud.key}
                value={aud.key}
                className="flex min-w-0 items-center justify-center gap-2 rounded-xl px-3 py-3 text-sm font-semibold data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-md sm:px-3 sm:py-2.5"
              >
                <Icon className="h-4 w-4 shrink-0" />
                <span className="truncate">{aud.tabLabel}</span>
              </TabsTrigger>
            );
          })}
        </TabsList>

        {AUDIENCES.map((aud) => (
          <TabsContent key={aud.key} value={aud.key} className="mt-0">
            <motion.div
              key={aud.key}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35 }}
              className="glass-card glass-refraction relative overflow-hidden p-6 sm:p-10"
            >
              {/* Decorative mesh */}
              <div
                className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full opacity-25 blur-[90px]"
                style={{
                  background:
                    "radial-gradient(circle, hsl(243 75% 59% / 0.6), transparent 70%)",
                }}
              />

              <div className="relative z-10">
                <h3 className="text-2xl font-extrabold tracking-tight text-foreground sm:text-3xl">
                  {aud.title}
                </h3>
                <p className="mt-2 max-w-2xl text-sm text-muted-foreground sm:text-base">
                  {aud.subtitle}
                </p>

                <div className="mt-8 grid gap-4 sm:grid-cols-3">
                  {aud.benefits.map((b, i) => {
                    const Icon = b.icon;
                    return (
                      <motion.div
                        key={b.title}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 + i * 0.08 }}
                        className="rounded-2xl border border-border/50 bg-secondary/30 p-5 transition-all hover:border-primary/40 hover:bg-secondary/50"
                      >
                        <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
                          <Icon className="h-5 w-5 text-primary" />
                        </div>
                        <h4 className="text-sm font-bold text-foreground">{b.title}</h4>
                        <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">
                          {b.description}
                        </p>
                      </motion.div>
                    );
                  })}
                </div>

                <div className="mt-8 flex justify-center">
                  <Button
                    size="lg"
                    asChild
                    className="group h-auto w-full max-w-md gap-1.5 whitespace-nowrap rounded-2xl px-3 py-3 text-center text-[12px] font-semibold leading-tight shadow-lg shadow-primary/20 transition-shadow hover:shadow-xl hover:shadow-primary/40 sm:w-auto sm:gap-2 sm:rounded-3xl sm:px-8 sm:py-6 sm:text-base"
                  >
                    <Link to={aud.cta.to} className="flex min-w-0 items-center justify-center gap-1.5 sm:gap-2">
                      <span className="sm:hidden">{aud.cta.mobileLabel}</span>
                      <span className="hidden sm:inline">{aud.cta.label}</span>
                      <ArrowRight className="h-3.5 w-3.5 shrink-0 transition-transform group-hover:translate-x-1 sm:h-4 sm:w-4" />
                    </Link>
                  </Button>
                </div>
              </div>
            </motion.div>
          </TabsContent>
        ))}
      </Tabs>
    </section>
  );
}
